import { useUserStore } from '@/stores/user'

/**
 * 检查用户是否拥有指定权限
 * @param {string|string[]} permissions - 权限代码或权限代码数组
 * @param {boolean} requireAll - 是否需要满足所有权限
 * @returns {boolean}
 */
export function hasPermission(permissions, requireAll = false) {
  const userStore = useUserStore()
  const userPermissions = userStore.permissions || []

  if (!permissions || userPermissions.length === 0) {
    return false
  }

  const permissionList = Array.isArray(permissions) ? permissions : [permissions]

  // 新旧权限别名映射（前端容错）：当新权限尚未下发但旧权限已存在时允许通过
  const aliasMap = {
    'stock_order.view': ['stock:view'],
    'stock_order.edit': ['stock:manage'],
    'stock_order.create': ['stock:manage'],
    'stock_order.cancel': ['stock:manage'],
    'stock_order.approve': ['stock:approve'],
    'batch:view': ['stock:view'],
    'batch:manage': ['stock:manage'],
    'inventory:adjust': ['stock:manage']
  }

  const contains = (code) => {
    if (userPermissions.includes(code)) return true
    const aliases = aliasMap[code]
    if (aliases && aliases.some(a => userPermissions.includes(a))) {
      console.log('[Permission][AliasHint] 命中新旧权限别名映射:', code, '→', aliases.filter(a => userPermissions.includes(a)))
      return true
    }
    return false
  }

  if (requireAll) {
    const ok = permissionList.every(code => contains(code))
    if (!ok) {
      const missing = permissionList.filter(code => !contains(code))
      console.warn('[Permission][Denied][All] 缺失权限:', missing, '现有权限数:', userPermissions.length)
    }
    return ok
  } else {
    const ok = permissionList.some(code => contains(code))
    if (!ok) {
      console.warn('[Permission][Denied][Any] 需要任一权限:', permissionList, '现有权限数:', userPermissions.length)
    }
    return ok
  }
}

/**
 * 检查用户是否拥有指定角色
 * @param {string|string[]} roles - 角色代码或角色代码数组
 * @param {boolean} requireAll - 是否需要满足所有角色
 * @returns {boolean}
 */
export function hasRole(roles, requireAll = false) {
  const userStore = useUserStore()
  const userRoles = userStore.roles

  if (!roles || !userRoles) {
    return false
  }

  const roleList = Array.isArray(roles) ? roles : [roles]

  if (requireAll) {
    return roleList.every(role => userRoles.includes(role))
  } else {
    return roleList.some(role => userRoles.includes(role))
  }
}

/**
 * 检查用户是否拥有指定用户组
 * @param {string|string[]} groups - 用户组代码或用户组代码数组
 * @param {boolean} requireAll - 是否需要满足所有用户组
 * @returns {boolean}
 */
export function hasGroup(groups, requireAll = false) {
  const userStore = useUserStore()
  const userGroups = userStore.groups

  if (!groups || !userGroups) {
    return false
  }

  const groupList = Array.isArray(groups) ? groups : [groups]

  if (requireAll) {
    return groupList.every(group => userGroups.includes(group))
  } else {
    return groupList.some(group => userGroups.includes(group))
  }
}

/**
 * 获取用户可访问的路由
 * @param {Array} routes - 路由配置
 * @returns {Array} 过滤后的路由
 */
export function filterAsyncRoutes(routes) {
  const res = []
  
  routes.forEach(route => {
    const tmp = { ...route }
    if (hasPermissionRoute(tmp)) {
      if (tmp.children) {
        tmp.children = filterAsyncRoutes(tmp.children)
      }
      res.push(tmp)
    }
  })
  
  return res
}

/**
 * 检查路由是否有权限访问
 * @param {Object} route - 路由对象
 * @returns {boolean}
 */
function hasPermissionRoute(route) {
  if (route.meta) {
    const { permission, role, group } = route.meta
    
    // 检查权限
    if (permission) {
      return hasPermission(permission)
    }
    
    // 检查角色
    if (role) {
      return hasRole(role)
    }
    
    // 检查用户组
    if (group) {
      return hasGroup(group)
    }
  }
  
  return true
}

/**
 * 权限指令
 */
export default {
  mounted(el, binding) {
    const { value } = binding
    
    if (value) {
      let hasAccess = false
      
      if (value.permission) {
        hasAccess = hasPermission(value.permission, value.requireAll)
      } else if (value.role) {
        hasAccess = hasRole(value.role, value.requireAll)
      } else if (value.group) {
        hasAccess = hasGroup(value.group, value.requireAll)
      }
      
      if (!hasAccess) {
        el.parentNode && el.parentNode.removeChild(el)
      }
    } else {
      throw new Error(`需要指定权限代码! 例如 v-permission="{ permission: 'xxx' }"`)
    }
  }
} 